import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, Field, Formik } from "formik";
import * as yup from "yup";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import axios from "../../services/axios";
import ReactMde from "react-mde";
import swal from "sweetalert";
import isConsultant from "../../services/isConsultant";

// Custom Imports
import Dashboard from "../../layouts/Dashboard";
import { useHistory, useParams } from "react-router-dom";
import saveLog from "../../services/saveLog";
import { Card } from "antd";

const Transparent = styled.label`
  opacity: 0;
`;
const DocumentList = styled.div`
  margin-bottom: 50px;
`;
const DocumentBackground = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
  background: #e0e0e0;
  &:hover {
    background: #d1d1d1;
  }
`;
const DocumentIcon = styled.i`
  font-size: 48px;
`;
const DocumentButtonIcon = styled.i`
  font-size: 24px;
`;
const DeleteButton = styled.div`
  margin-left: auto;
  cursor: pointer;
  color: #eba09b;
  &:hover {
    color: #ed6f66;
  }
`;
const DocumentDescriptor = styled.div`
  margin-left: 25px;
`;

function Document({ document, index, deleteDocument }) {
  // Effects and Events
  function onClickDeleteDocument() {
    deleteDocument(index);
  }

  return (
    <div>
   {!isConsultant() &&  <DocumentBackground>
      <DocumentIcon className="fa fa-file" />
      <DocumentDescriptor>
        <h5>
          {document.title} {document.mandatory && "*"}
        </h5>
        <div>Extensions: {document.extensions}</div>
        <div>File Size: {document.size}mb</div>
      </DocumentDescriptor>
      <DeleteButton onClick={onClickDeleteDocument}>
        <DocumentButtonIcon className="fa fa-trash" />
      </DeleteButton>
    </DocumentBackground>}
    </div>
  );
}

const MutedHint = styled.p`
  color: #bbb;
  font-size: 12px;
`;

function DocumentFormPreview({ values, documents, inputs = [] }) {
  return (
    <>
      {!isConsultant() && <Card className="border mb-2" title={values.title}>
        <div>
          <ReactMarkdown>{values.description}</ReactMarkdown>
          {inputs.map((input) => (
            <div className="form-group">
              <label>
                {input.name} {input.mandatory && "(*)"}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={"Please enter " + input.name}
              />
            </div>
          ))}
          {documents.map((document, key) => (
            <div className="form-group" key={key}>
              <label>{document.title}</label>
              <input className="form-control" type="file" />
              <MutedHint className="mt-2">
                Allowed {document.extensions} upto {document.size}mb
              </MutedHint>
            </div>
          ))}
        </div>
        <div className="mt-2">
          <button
            className="btn btn-primary"
            onClick={(e) => alert("This is just the form preview.")}
          >
            Upload
          </button>
        </div>
      </Card>}
    </>
  );
}

function DocumentFormsEdit() {
  // Stateful Hooks
  const [initialValues, setInitialValues] = useState({
    title: "Form Name",
    description: "Form Description",
  });
  const validationSchema = yup.object({
    title: yup.string().min(3).required().label("Title"),
    description: yup.string().min(3).required().label("Description"),
  });
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState();
  const [addDocumentError, setAddDocumentError] = useState();
  const titleRef = useRef();
  const extensionsRef = useRef();
  const sizeRef = useRef();
  const mandatoryRef = useRef();
  const { id } = useParams();
  const [inputs, setInputs] = useState([]);
  const inputNameRef = useRef();
  const inputManadatoryRef = useRef();

  // Effects and Events
  useEffect(() => {
    (async () => {
      const { data } = await axios.get("/documents/" + id, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("user.token")}`,
        },
      });
      setDocuments(
        data.documents.map((document) => {
          document.extensions = document.extensions.join(",");
          return document;
        })
      );
      setInputs(data.inputs || []);
      setInitialValues(data);
    })();
  }, []);

  function onClickAddDocument() {
    setAddDocumentError();
    const title = titleRef.current.value;
    const extensions = extensionsRef.current.value;
    const size = sizeRef.current.value;
    const mandatory = mandatoryRef.current.checked;

    if (title === "") {
      setAddDocumentError("Please enter a title");
    } else if (extensions === "") {
      setAddDocumentError("Please enter extensions.");
    } else if (size === "") {
      setAddDocumentError("Please enter a file size");
    } else if (size <= 0) {
      setAddDocumentError("Ha Ha. Nice Try. Enter a positive file size limit.");
    } else {
      setDocuments([...documents, { title, extensions, size, mandatory }]);
    }
  }

  function onClickAddInput() {
    setError();
    const name = inputNameRef.current.value;
    const mandatory = inputManadatoryRef.current.checked;
    if (name === "") {
      setError("Please enter input name");
    } else {
      setInputs([...inputs, { name, mandatory }]);
      inputNameRef.current.value = "";
    }
  }

  function deleteDocument(index) {
    documents.splice(index, 1);
    setDocuments([...documents]);
  }

  async function saveDocumentForm(values) {
    setError();

    swal({
      title: "Updated successfully",
      icon: "success",
    });

    if (documents.length + inputs.length === 0) {
      setError("You must add atleast 1 document/input field to continue.");
      return;
    }
    const payload = {
      ...values,
      documents: documents.map((doc) => {
        doc.extensions = doc.extensions.toLowerCase().split(",");
        doc.extensions.map((extension) => {
          return extension.trim();
        });
        return doc;
      }),
      inputs,
    };
    try {
      saveLog("JOINING_FORM_EDIT", `Edited a joining form ${values.title}`);
      await axios.patch("/documents/" + id, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("user.token")}`,
        },
      });
    } catch (e) {
      console.error(e.response.data);
    }
  }

  return (
    <div>
    {!isConsultant() && <Dashboard title="Joining Form Edit">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={saveDocumentForm}
        enableReinitialize
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <div className="row">
            <div className="col-lg-6 col-md-12">
              <Card title="Form Options" className="border mb-4">
                <div>
                  <div className="form-group">
                    <label htmlFor="title">Title</label>
                    <Field name="title" className="form-control" type="text" />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="description">
                      Description <small>(Markdown Supported)</small>
                    </label>
                    <ReactMde
                      value={values.description}
                      onChange={(changes) =>
                        setFieldValue("description", changes)
                      }
                      childProps={{
                        previewButton: {
                          style: {
                            display: "none",
                          },
                        },
                        writeButton: {
                          style: {
                            display: "none",
                          },
                        },
                      }}
                    />
                    {/* <Field name="description" className="form-control" as="textarea" /> */}
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <hr />
                  <label>
                    <strong>Files</strong>
                  </label>
                  {documents.length === 0 ? (
                    <p>Please add one or more documents.</p>
                  ) : (
                    <DocumentList>
                      {documents.map((doc, key) => (
                        <Document
                          document={doc}
                          key={key}
                          index={key}
                          deleteDocument={deleteDocument}
                        />
                      ))}
                    </DocumentList>
                  )}
                  <hr />
                  <label>
                    <strong>Add a File</strong>
                  </label>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="documentTitle">Document Name</label>
                        <input
                          ref={titleRef}
                          className="form-control"
                          id="documentTitle"
                          type="text"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="documentExtension">
                          Allowed Extensions
                        </label>
                        <select
                          ref={extensionsRef}
                          id="documentExtension"
                          className="form-control"
                        >
                          <option value="jpg,png,pdf">
                            Images (jpg, png, pdf)
                          </option>
                          <option value="doc,docx,xls,xlsx,pdf">
                            Documents (doc, docx, xls, xlsx, pdf)
                          </option>
                          <option value="mp4">Video (mp4)</option>
                          <option value="doc,docx,xls,xlsx,pdf,jpg,png,mp4">
                            All Allowed Types
                          </option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="documentSize">File Size Limit</label>
                        <input
                          ref={sizeRef}
                          className="form-control"
                          id="documentSize"
                          type="number"
                        />
                      </div>
                    </div>
                    <div className="col-md-12">
                      <div className="form-group">
                        <input
                          ref={mandatoryRef}
                          id="mandatory"
                          type="checkbox"
                        />{" "}
                        <label htmlFor="mandatory">Mandatory</label>
                      </div>
                    </div>
                    <div className=" col-md-12">
                      <button
                        className="btn btn-primary btn-block"
                        onClick={onClickAddDocument}
                      >
                        Add File To Form
                      </button>
                    </div>
                    {addDocumentError ? (
                      <div className="col-md-12">
                        <div className="text-danger">{addDocumentError}</div>
                      </div>
                    ) : null}
                  </div>

                  {inputs.length > 0 && (
                    <>
                      <hr />
                      <label>
                        <strong>Inputs</strong>
                      </label>
                    </>
                  )}

                  {inputs.map((input, index) => (
                    <div className="form-group">
                      <label>
                        {input.name} {input.mandatory && "(*)"}
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder={"Please enter " + input.name}
                        />
                        <div className="input-group-append">
                          <button
                            className="btn btn-danger"
                            onClick={() => {
                              inputs.splice(index, 1);
                              setInputs([...inputs]);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  <hr />
                  <label>
                    <strong>Add an Input</strong>
                  </label>
                  <div className="form-group">
                    <label htmlFor="inputTitle">Input Name</label>
                    <input
                      ref={inputNameRef}
                      className="form-control"
                      id="inputTitle"
                      type="text"
                    />
                  </div>
                  <div className="form-group">
                    <input
                      ref={inputManadatoryRef}
                      id="inputMandatory"
                      type="checkbox"
                    />{" "}
                    <label htmlFor="inputMandatory">Mandatory</label>
                  </div>
                  <button
                    className="btn btn-primary btn-block"
                    onClick={onClickAddInput}
                  >
                    Add Input to Form
                  </button>
                  {error ? (
                    <div className="alert alert-danger mt-3">{error}</div>
                  ) : null}
                </div>
                <div className="mt-2">
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    Update
                  </button>
                </div>
              </Card>
            </div>
            <div className="col-lg-6 col-md-12">
              <DocumentFormPreview
                values={values}
                documents={documents}
                inputs={inputs}
              />
            </div>
          </div>
        )}
      </Formik>
    </Dashboard>}
    </div>
  );
}

export default DocumentFormsEdit;
